import React from "react";

/* ─── Primitive building blocks ──────────────────────────────────── */

/** A rectangular shimmer block. Pass width/height/style to shape it. */
export const SkeletonBlock = ({ width, height, radius, style, dark }) => (
  <div
    className={dark ? "ecl-skeleton-dark" : "ecl-skeleton"}
    style={{
      width: width || "100%",
      height: height || 16,
      borderRadius: radius || 6,
      flexShrink: 0,
      ...style,
    }}
  />
);

/** A stack of text-line skeletons — mimics a paragraph. */
export const SkeletonLines = ({ lines = 3, dark, lastWidth = "60%" }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
    {Array.from({ length: lines }).map((_, i) => (
      <SkeletonBlock
        key={i}
        dark={dark}
        height={14}
        width={i === lines - 1 ? lastWidth : `${95 - i * 5}%`}
      />
    ))}
  </div>
);


/* ─── Empty State ──────────────────────────────────────────────────
   Usage:
     <EmptyState
       title="No posts yet"
       description="Check back soon for the latest insights."
       action={{ label: "Go home", href: "/" }}
     />
   ─────────────────────────────────────────────────────────────────── */
export const EmptyState = ({
  title = "Nothing here yet",
  description = "It looks like there's no content to display right now.",
  action,
  icon,
}) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "80px 24px",
      textAlign: "center",
    }}
  >
    {/* Icon */}
    <div
      style={{
        width: 72,
        height: 72,
        borderRadius: "50%",
        background: "#EFF6FF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 24,
      }}
    >
      {icon || (
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#009EDC"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      )}
    </div>

    <h3
      style={{
        fontSize: "1.25rem",
        fontWeight: 700,
        color: "#0a1a3a",
        marginBottom: 8,
      }}
    >
      {title}
    </h3>

    <p
      style={{
        fontSize: "0.95rem",
        color: "#6B7280",
        maxWidth: 420,
        lineHeight: 1.7,
        marginBottom: action ? 28 : 0,
      }}
    >
      {description}
    </p>

    {action && (
      <a
        href={action.href || "/"}
        className="btn btn-dark"
        style={{ borderRadius: 6, padding: "10px 24px", fontSize: "0.875rem" }}
      >
        {action.label}
      </a>
    )}
  </div>
);


/* ─── Error State ──────────────────────────────────────────────────
   For failed API calls — has a "Go back" affordance.
   ─────────────────────────────────────────────────────────────────── */
export const ErrorState = ({ message, onBack }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "80px 24px",
      textAlign: "center",
    }}
  >
    <div
      style={{
        width: 72,
        height: 72,
        borderRadius: "50%",
        background: "#FEF2F2",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 24,
      }}
    >
      <svg
        width="32"
        height="32"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#EF4444"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    </div>

    <h3
      style={{ fontSize: "1.25rem", fontWeight: 700, color: "#0a1a3a", marginBottom: 8 }}
    >
      Something went wrong
    </h3>

    <p
      style={{
        fontSize: "0.95rem",
        color: "#6B7280",
        maxWidth: 400,
        lineHeight: 1.7,
        marginBottom: 28,
      }}
    >
      {message || "We couldn't load this content. Please try again."}
    </p>

    {onBack && (
      <button
        className="btn btn-outline-dark"
        style={{ borderRadius: 6, padding: "10px 24px", fontSize: "0.875rem" }}
        onClick={onBack}
      >
        Go back
      </button>
    )}
  </div>
);


/* ─── Blog Page Skeleton ───────────────────────────────────────────
   Mirrors: dark hero (featured cards) + white latest section
   ─────────────────────────────────────────────────────────────────── */
export const BlogPageSkeleton = () => (
  <>
    {/* Dark hero */}
    <section style={{ background: "#050608", padding: "60px 0 80px" }}>
      <div className="container">
        {/* Page title */}
        <SkeletonBlock dark width={140} height={44} style={{ marginBottom: 10 }} />
        <SkeletonBlock dark width={260} height={18} style={{ marginBottom: 40 }} />

        {/* Featured cards row */}
        <div className="row g-4">
          <div className="col-md-8">
            <SkeletonBlock dark height={360} radius={12} />
          </div>
          <div className="col-md-4">
            <SkeletonBlock dark height={360} radius={12} />
          </div>
        </div>
      </div>
    </section>

    {/* Latest section */}
    <section style={{ padding: "60px 0" }}>
      <div className="container">
        <SkeletonBlock width={100} height={28} style={{ marginBottom: 28 }} />
        <div className="row g-4">
          {[0, 1, 2].map(i => (
            <div key={i} className="col-md-4">
              <div
                style={{
                  borderRadius: 10,
                  overflow: "hidden",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
              >
                <SkeletonBlock height={220} radius={0} />
                <div style={{ padding: "16px 16px 20px" }}>
                  <SkeletonBlock width={80} height={12} style={{ marginBottom: 10 }} />
                  <SkeletonBlock height={18} style={{ marginBottom: 8 }} />
                  <SkeletonBlock width="75%" height={18} style={{ marginBottom: 12 }} />
                  <SkeletonBlock width={90} height={12} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  </>
);


/* ─── Blog Details Skeleton ────────────────────────────────────────
   Mirrors: back button + wide hero image + centered article content
   ─────────────────────────────────────────────────────────────────── */
export const BlogDetailsSkeleton = () => (
  <>
    {/* Back button */}
    <div style={{ width: "60%", margin: "32px auto 20px" }}>
      <SkeletonBlock width={90} height={36} />
    </div>

    {/* Hero image */}
    <div style={{ width: "60%", margin: "0 auto 48px" }}>
      <SkeletonBlock height={460} radius={10} />
    </div>

    {/* Article content */}
    <div className="container">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <SkeletonBlock width={100} height={13} style={{ marginBottom: 14 }} />
          <SkeletonBlock width="90%" height={36} style={{ marginBottom: 10 }} />
          <SkeletonBlock width="65%" height={36} style={{ marginBottom: 14 }} />
          <SkeletonBlock width={120} height={13} style={{ marginBottom: 28 }} />
          <SkeletonBlock height={1} radius={0} style={{ background: "#e5e7eb", marginBottom: 28 }} />
          <SkeletonLines lines={5} lastWidth="45%" />
          <div style={{ marginTop: 28 }}>
            <SkeletonLines lines={4} lastWidth="70%" />
          </div>
          <div style={{ marginTop: 28 }}>
            <SkeletonLines lines={3} lastWidth="55%" />
          </div>
        </div>
      </div>
    </div>
  </>
);


/* ─── Case Studies Skeleton ────────────────────────────────────────
   Mirrors: hero section + 2-col featured + 3-col rest
   ─────────────────────────────────────────────────────────────────── */
export const CaseStudiesSkeleton = () => (
  <main>
    {/* Hero placeholder — same height as real hero */}
    <div style={{ height: 520, background: "#0a1a3a", position: "relative", overflow: "hidden" }}>
      <div
        className="ecl-skeleton-dark"
        style={{ position: "absolute", inset: 0, borderRadius: 0 }}
      />
      {/* Floating card skeleton */}
      <div
        style={{
          position: "absolute",
          left: "10%",
          top: "50%",
          transform: "translateY(-50%)",
          background: "rgba(255,255,255,0.08)",
          backdropFilter: "blur(10px)",
          padding: "36px 42px",
          borderRadius: 10,
          width: 480,
        }}
      >
        <SkeletonBlock dark width={180} height={38} style={{ marginBottom: 14 }} />
        <SkeletonBlock dark height={14} style={{ marginBottom: 8 }} />
        <SkeletonBlock dark width="80%" height={14} />
      </div>
    </div>

    {/* Content */}
    <section className="container" style={{ paddingTop: 80, paddingBottom: 100 }}>
      <SkeletonBlock width={80} height={32} style={{ marginBottom: 36 }} />

      {/* 2-col featured */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2,1fr)",
          gap: 32,
          marginBottom: 40,
        }}
      >
        {[0, 1].map(i => (
          <div key={i} style={{ borderRadius: 16, overflow: "hidden", boxShadow: "0 4px 16px rgba(0,0,0,0.07)" }}>
            <SkeletonBlock height={260} radius={0} />
            <div style={{ padding: 22 }}>
              <SkeletonBlock width={90} height={12} style={{ marginBottom: 10 }} />
              <SkeletonBlock height={20} style={{ marginBottom: 8 }} />
              <SkeletonBlock width="60%" height={20} style={{ marginBottom: 10 }} />
              <SkeletonBlock width={70} height={12} />
            </div>
          </div>
        ))}
      </div>

      {/* 3-col rest */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 32 }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{ borderRadius: 16, overflow: "hidden", boxShadow: "0 4px 16px rgba(0,0,0,0.07)" }}>
            <SkeletonBlock height={200} radius={0} />
            <div style={{ padding: 22 }}>
              <SkeletonBlock width={70} height={12} style={{ marginBottom: 10 }} />
              <SkeletonBlock height={18} style={{ marginBottom: 8 }} />
              <SkeletonBlock width="55%" height={18} style={{ marginBottom: 10 }} />
              <SkeletonBlock width={60} height={12} />
            </div>
          </div>
        ))}
      </div>
    </section>
  </main>
);


/* ─── Trainings Page Skeleton ──────────────────────────────────────
   Mirrors: dark hero + stats strip + filter pills + course card grid
   ─────────────────────────────────────────────────────────────────── */
export const TrainingsPageSkeleton = () => (
  <>
    {/* Hero */}
    <section style={{ background: "#050b24", padding: "100px 0 80px" }}>
      <div className="container">
        <SkeletonBlock dark width={220} height={12} style={{ marginBottom: 18 }} />
        <SkeletonBlock dark width="60%" height={52} style={{ marginBottom: 12 }} />
        <SkeletonBlock dark width="45%" height={52} style={{ marginBottom: 24 }} />
        <SkeletonBlock dark width={480} height={18} style={{ marginBottom: 8 }} />
        <SkeletonBlock dark width={360} height={18} style={{ marginBottom: 44 }} />
        {/* Search bar */}
        <SkeletonBlock dark width={540} height={52} radius={10} />
      </div>
    </section>

    {/* Stats strip */}
    <section style={{ background: "#0a1a3a", padding: "30px 0" }}>
      <div className="container">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, textAlign: "center" }}>
          {[0, 1, 2, 3].map(i => (
            <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
              <SkeletonBlock dark width={60} height={28} />
              <SkeletonBlock dark width={100} height={12} />
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Filter + grid */}
    <section style={{ padding: "60px 0 80px", background: "#f8f9fc" }}>
      <div className="container">
        {/* Filter pills */}
        <div style={{ display: "flex", gap: 10, marginBottom: 32 }}>
          {[80, 60, 100, 50, 110].map((w, i) => (
            <SkeletonBlock key={i} width={w} height={36} radius={50} />
          ))}
        </div>

        {/* Card grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))",
            gap: 28,
          }}
        >
          {[0, 1, 2, 3, 4, 5].map(i => (
            <div
              key={i}
              style={{
                background: "#fff",
                borderRadius: 14,
                overflow: "hidden",
                boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
              }}
            >
              <SkeletonBlock height={176} radius={0} />
              <div style={{ padding: "20px 22px" }}>
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  <SkeletonBlock width={60} height={20} radius={4} />
                  <SkeletonBlock width={80} height={20} />
                </div>
                <SkeletonBlock height={18} style={{ marginBottom: 8 }} />
                <SkeletonBlock width="80%" height={18} style={{ marginBottom: 14 }} />
                <SkeletonBlock height={13} style={{ marginBottom: 6 }} />
                <SkeletonBlock width="65%" height={13} style={{ marginBottom: 18 }} />
                <div style={{ display: "flex", gap: 16, marginBottom: 18 }}>
                  <SkeletonBlock width={70} height={13} />
                  <SkeletonBlock width={90} height={13} />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <SkeletonBlock height={36} radius={6} />
                  <SkeletonBlock height={36} radius={6} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  </>
);


/* ─── Service Details Skeleton ─────────────────────────────────────
   Mirrors: back button + hero + stats + case studies section
   ─────────────────────────────────────────────────────────────────── */
export const ServiceDetailsSkeleton = () => (
  <main className="container" style={{ paddingTop: 40 }}>
    {/* Back button */}
    <SkeletonBlock width={90} height={36} style={{ marginBottom: 24 }} />

    {/* Hero */}
    <div
      style={{
        position: "relative",
        height: 520,
        borderRadius: 12,
        overflow: "hidden",
        marginBottom: 80,
      }}
    >
      <SkeletonBlock height={520} radius={12} style={{ position: "absolute", inset: 0 }} />
      {/* Floating card */}
      <div
        style={{
          position: "absolute",
          left: "8%",
          top: "50%",
          transform: "translateY(-50%)",
          background: "rgba(255,255,255,0.92)",
          padding: "32px 40px",
          borderRadius: 10,
          width: 460,
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
        }}
      >
        <SkeletonBlock width={200} height={32} style={{ marginBottom: 16 }} />
        <SkeletonBlock height={14} style={{ marginBottom: 8 }} />
        <SkeletonBlock width="80%" height={14} />
      </div>
    </div>

    {/* Stats */}
    <div style={{ marginBottom: 80 }}>
      <SkeletonBlock width="60%" height={28} style={{ marginBottom: 16 }} />
      <SkeletonBlock width="50%" height={28} />
    </div>

    {/* Case studies */}
    <div style={{ marginBottom: 80 }}>
      <SkeletonBlock width={220} height={32} style={{ marginBottom: 28 }} />
      {[0, 1].map(i => (
        <div
          key={i}
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            borderRadius: 14,
            overflow: "hidden",
            marginBottom: 24,
            boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
          }}
        >
          <div style={{ padding: 28 }}>
            <SkeletonBlock width={90} height={12} style={{ marginBottom: 14 }} />
            <SkeletonBlock height={22} style={{ marginBottom: 10 }} />
            <SkeletonBlock width="70%" height={22} style={{ marginBottom: 20 }} />
            <SkeletonBlock width={80} height={14} />
          </div>
          <SkeletonBlock height={220} radius={0} />
        </div>
      ))}
    </div>
  </main>
);
